import { Download, Eye, HardDrive, History, Link2, ShieldCheck, X } from 'lucide-react';
import type { Nodo } from '@doc/shared';
import { CLAVES_PERMISO, ETIQUETA_PERMISO, ETIQUETA_TIPO_VISTA, nivel, tipoDeVista } from '@doc/shared';
import { fecha, iconoDe, tamano } from '../../lib/formato.js';
import { urlArchivo } from '../../lib/api.js';
import { BarraCuota } from '../../ui/BarraCuota.js';
import { useCuotaDeNodo } from './consultas.js';

interface Props {
  nodo: Nodo | null;
  cantidadSeleccion: number;
  alCerrar(): void;
  alVerPermisos(): void;
  alVerVersiones(): void;
  alVer(): void;
  alCompartir(): void;
  alDescargar(): void;
}

export function PanelDetalles({
  nodo,
  cantidadSeleccion,
  alCerrar,
  alVerPermisos,
  alVerVersiones,
  alVer,
  alCompartir,
  alDescargar,
}: Props) {
  if (!nodo) {
    return (
      <aside className="detalles">
        <div className="muted" style={{ fontSize: 12.5, textAlign: 'center', padding: '30px 0' }}>
          {cantidadSeleccion > 1 ? `${cantidadSeleccion} elementos seleccionados` : 'Selecciona un elemento para ver su detalle'}
        </div>
      </aside>
    );
  }

  const { Icono, clase } = iconoDe(nodo);
  const esImagen = nodo.mime?.startsWith('image/');
  const previsualizable = nodo.tipo === 'ARCHIVO' && tipoDeVista(nodo.mime, nodo.nombre) !== 'NINGUNO';

  return (
    <aside className="detalles">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h2 style={{ flex: 1, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--dim)' }}>
          Detalles
        </h2>
        <button className="btn btn--fantasma btn--icono btn--sm" onClick={alCerrar} title="Ocultar panel">
          <X size={14} />
        </button>
      </div>

      <div className="detalles__previa">
        {esImagen ? (
          <img src={urlArchivo(nodo.id, 'vista')} alt={nodo.nombre} />
        ) : (
          <span className={`icono-tipo ${clase}`} style={{ background: 'transparent' }}>
            <Icono size={56} strokeWidth={1.2} />
          </span>
        )}
      </div>

      <h3>{nodo.nombre}</h3>
      <div className="muted" style={{ fontSize: 11.5, marginBottom: 12 }}>
        {nodo.tipo === 'CARPETA'
          ? `Carpeta · ${nodo.hijos ?? 0} elementos`
          : `${ETIQUETA_TIPO_VISTA[tipoDeVista(nodo.mime, nodo.nombre)]} · ${tamano(nodo.bytes)}`}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {nodo.tipo === 'ARCHIVO' && (
          <>
            <button className="btn btn--sm" onClick={alDescargar}>
              <Download size={13} /> Descargar
            </button>
            {previsualizable && (
              <button className="btn btn--sm" onClick={alVer}>
                <Eye size={13} /> Vista previa
              </button>
            )}
            <button className="btn btn--sm" onClick={alVerVersiones}>
              <History size={13} /> v{nodo.version}
            </button>
          </>
        )}
        {nodo.permisos.compartir && (
          <button className="btn btn--sm" onClick={alCompartir}>
            <Link2 size={13} /> Compartir
          </button>
        )}
        {nodo.permisos.gestionar && (
          <button className="btn btn--sm" onClick={alVerPermisos}>
            <ShieldCheck size={13} /> Permisos
          </button>
        )}
      </div>

      <div className="dato">
        <span className="k">Creado por</span>
        <span className="v">{nodo.creadoPorNombre ?? '—'}</span>
      </div>
      <div className="dato">
        <span className="k">Creado</span>
        <span className="v">{fecha(nodo.creadoEn)}</span>
      </div>
      <div className="dato">
        <span className="k">Modificado</span>
        <span className="v">{fecha(nodo.actualizadoEn)}</span>
      </div>
      {nodo.tipo === 'ARCHIVO' && (
        <div className="dato">
          <span className="k">Tipo</span>
          <span className="v">{nodo.mime ?? '—'}</span>
        </div>
      )}
      <div className="dato">
        <span className="k">Permisos</span>
        <span className="v">{nodo.heredaPermisos ? 'Heredados' : 'Propios'}</span>
      </div>

      {nodo.tipo === 'CARPETA' && <SeccionCuota nodoId={nodo.id} />}

      <div className="detalles__seccion">
        <h4>Lo que puedes hacer acá</h4>
        <div className="chips-permisos">
          {CLAVES_PERMISO.filter((c) => nodo.permisos[c]).map((c) => (
            <span key={c} className="badge badge--verde">
              {ETIQUETA_PERMISO[c]}
            </span>
          ))}
          {!nodo.permisos.leer && <span className="badge">Sin acceso</span>}
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          Nivel efectivo: {nivel(nodo.permisos)}
        </div>
      </div>
    </aside>
  );
}

/**
 * Cuota de la carpeta. Se muestra la propia si la tiene y, si no, la del
 * ancestro que la limita: para quien sube archivos lo relevante es cuánto
 * espacio le queda, no en qué nivel del árbol está el tope.
 */
function SeccionCuota({ nodoId }: { nodoId: string }) {
  const { data } = useCuotaDeNodo(nodoId);
  const cuota = data?.propia ?? data?.efectiva;
  if (!cuota) return null;

  const heredada = !data?.propia;
  return (
    <div className="detalles__seccion">
      <h4>
        <HardDrive size={11} style={{ marginRight: 5, verticalAlign: -1 }} />
        Almacenamiento
      </h4>
      <BarraCuota
        usadoBytes={cuota.usadoBytes}
        cuotaBytes={cuota.cuotaBytes}
        estado={cuota.estado}
        porcentaje={cuota.porcentaje}
      />
      <div className="muted" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
        {heredada ? <>Límite heredado de <strong>{cuota.nombre}</strong>. </> : null}
        {cuota.libreBytes !== null && cuota.libreBytes > 0
          ? `Quedan ${tamano(cuota.libreBytes)} libres.`
          : 'Sin espacio: no se puede subir nada más hasta liberar.'}
        {cuota.papeleraBytes > 0 && ` ${tamano(cuota.papeleraBytes)} están en la papelera.`}
      </div>
    </div>
  );
}
